<template>
  <settings-section
    v-if="allowSendMessages !== undefined"
    :title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_UPDATE_TITLE')"
    :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_UPDATE_SUB_TEXT')"
  >
    <form @submit.prevent="updateInbox">
      <woot-avatar-uploader
        :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_AVATAR.LABEL')"
        :src="avatarUrl"
        class="settings-item"
        delete-avatar
        @change="handleImageUpload"
        @onAvatarDelete="handleAvatarDelete"
      />
      <woot-input-channel-name
        v-model.trim="selectedInboxName"
        class="medium-9 columns settings-item"
        :class="{ error: $v.selectedInboxName.$error }"
        :label="$t('INBOX_MGMT.ADD.CHANNEL_NAME.LABEL')"
        :placeholder="$t('INBOX_MGMT.ADD.CHANNEL_NAME.PLACEHOLDER')"
        @blur="$v.selectedInboxName.$touch"
      />
      <phone-number-status
        :is-phone-number-linked.sync="linked"
        :value="inbox.phone_number"
        :phone-number-change-status="phoneNumberChangeStatus"
      />

      <label class="medium-9 columns settings-item">
        {{ $t('INBOX_MGMT.ADD.EVOLUTION_API.ALLOW_SEND_MESSAGE.LABEL') }}
        <select v-model="allowSendMessages">
          <option :value="true">
            {{ $t('INBOX_MGMT.ADD.EVOLUTION_API.ALLOW_SEND_MESSAGE.ENABLED') }}
          </option>
          <option :value="false">
            {{ $t('INBOX_MGMT.ADD.EVOLUTION_API.ALLOW_SEND_MESSAGE.DISABLED') }}
          </option>
        </select>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.EVOLUTION_API.ALLOW_SEND_MESSAGE.HELP_TEXT') }}
        </p>
      </label>

      <label class="medium-9 columns settings-item">
        {{
          $t(
            'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_ENABLE_DATA_EXTRACTION.LABEL'
          )
        }}
        <select v-model="enableDataExtraction">
          <option :value="true">
            {{
              $t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_ENABLE_DATA_EXTRACTION.ENABLED'
              )
            }}
          </option>
          <option :value="false">
            {{
              $t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_ENABLE_DATA_EXTRACTION.DISABLED'
              )
            }}
          </option>
        </select>
        <p class="help-text">
          {{
            $t(
              'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_ENABLE_DATA_EXTRACTION.HELP_TEXT'
            )
          }}
        </p>
      </label>

      <woot-submit-button
        type="submit"
        :disabled="$v.$invalid"
        :button-text="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
        :loading="isUpdating"
      />
    </form>
    <linked-modal
      :show-link-modal="showLinkModal"
      :show-alert-error.sync="showAlertErrorOnModal"
      :on-close="onClose"
      :qrcode.sync="qrcode"
      :display-refresh-overlay.sync="displayRefreshOverlay"
      :refresh-qr-code="refreshQrCode"
    />
    <unlinked-modal
      :show-unlink-modal="showUnlinkModal"
      :on-close="onClose"
      :on-confirm-unlink="onConfirmUnlink"
    />
  </settings-section>
</template>
<script>
import SettingsSection from '../../../../../components/SettingsSection';
import PhoneNumberStatus from '../components/PhoneNumberStatus.vue';
import LinkedModal from '../components/LinkedModal.vue';
import UnlinkedModal from '../components/UnlinkedModal';
import { required, maxLength } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import { mapState, mapActions } from 'vuex';

export default {
  components: {
    SettingsSection,
    PhoneNumberStatus,
    LinkedModal,
    UnlinkedModal,
  },
  mixins: [alertMixin],
  props: {
    inbox: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  data() {
    return {
      avatarFile: null,
      avatarUrl: this.inbox.avatar_url,
      linked: this.inbox.linked,
      selectedInboxName: this.inbox.name,
      allowSendMessages: this.inbox.allow_send_messages,
      enableDataExtraction: this.inbox.enable_data_extraction || false,
      isUpdating: false,
      showLinkModal: false,
      showAlertErrorOnModal: false,
      displayRefreshOverlay: false,
      qrcode: '',
      showUnlinkModal: false,
    };
  },
  computed: {
    // Mapea el estado global de 'inboxes' y accede a 'EVOLUTION_API_CONNECT'
    ...mapState('inboxes', ['EVOLUTION_API_CONNECT']),
    channelParams() {
      return {
        allow_send_messages: this.allowSendMessages,
        enable_data_extraction: this.enableDataExtraction,
        inbox_name: this.selectedInboxName,
        phone_number: this.inbox.phone_number,
        name: this.selectedInboxName,
        channel: {
          phone_number: this.inbox.phone_number,
          type: 'evolutionapi',
          linked: this.linked,
        },
      };
    },
  },
  watch: {
    // Maneja eventos de conexión de Evolution API
    async EVOLUTION_API_CONNECT(data) {
      // si se encuentra en la página de configuraciones
      if (this.$route.name === 'settings_inbox_show') {
        if (
          data.event === 'connected' &&
          data.instance === this.inbox.phone_number
        ) {
          // Cierra el modal y lanza evento para mostrar modal
          this.onClose();
          this.linked = true;
          this.displayRefreshOverlay = false;
          this.$emit(
            'onPhoneNumberLinked',
            this.$t('INBOX_MGMT.ADD.EVOLUTION_API.SUCCESS.LINKING_SUCCESS')
          );
          try {
            // si el número se vinculó correctamente se actualiza el valor en el state y la BD
            await this.updateLinkedNumberStatus();
          } catch (error) {
            console.error(error);
          }
        } else if (data.event === 'different_numbers') {
          // Muestra error si los números no coinciden
          this.showAlertErrorOnModal = true;
        } else if (
          data.event === 'qrcode_refused' &&
          data.instance === this.phoneNumber
        ) {
          this.displayRefreshOverlay = true;
        } else if (
          data.event === 'qrcode_updated' &&
          data.instance === this.phoneNumber &&
          data.data?.base64
        ) {
          this.displayRefreshOverlay = false;
          this.qrcode = data.data.base64;
        } else if (data.event === 'qrcode_limit_reached') {
          this.displayRefreshOverlay = true;
        }
      }
    },
  },
  mounted() {
    // en caso de que no se detecte inicialmente el atributo allow_send_messages se recarga nuevamente la página
    if (this.inbox.allow_send_messages === undefined) {
      window.location.reload();
    }
  },
  methods: {
    // Mapea la acción `evolutionApiConnect` del módulo `inboxes` para su uso en el componente
    ...mapActions('inboxes', ['evolutionApiConnect']),
    // Se conecta a una instancia de Evolution API y maneja la respuesta
    async connectEvolutionInstance() {
      try {
        const response = await this.$store.dispatch(
          'inboxes/connectEvolutionInstance',
          this.channelParams
        );
        this.qrcode = response.qrcode.base64;

        // Si no se obtiene un código QR para mostrar se muestra la opción de refrescar el QR
        if (!this.qrcode) this.displayRefreshOverlay = true;

        this.showAlertErrorOnModal = false;
      } catch (error) {
        this.showAlertErrorOnModal = true;
      }
    }, // Metodo para refrescar el codigo QR
    async refreshQrCode() {
      this.showAlertErrorOnModal = false;
      this.displayRefreshOverlay = false;
      await this.connectEvolutionInstance();
    },
    // Método para vincular o desvincular el número de teléfono
    async phoneNumberChangeStatus() {
      if (!this.linked) {
        try {
          await this.onShow();
          this.showLinkModal = true;
        } catch (error) {
          this.showLinkModal = false;
        }
      } else {
        // Muestra modal para desvincular intancia de evolution api
        this.showUnlinkModal = true;
      }
    },
    // Método que muestra el modal con el QR para la vinculación del número de teléfono
    async onShow() {
      try {
        await this.connectEvolutionInstance();
        this.showLinkModal = true;
      } catch (error) {
        this.showLinkModal = false;
      }
    },
    // Funcion para cerrar modals
    onClose() {
      if (this.showLinkModal) {
        this.showLinkModal = false;
      }

      if (this.showUnlinkModal) {
        this.showUnlinkModal = false;
      }
    },
    // Funcion para desvincular instancia en evolution api
    async onConfirmUnlink() {
      try {
        this.showUnlinkModal = false;
        await this.$store.dispatch('inboxes/unlinkEvolutionInstance', {
          phone_number: this.inbox.phone_number,
        });

        this.linked = false;
        await this.updateLinkedNumberStatus();
        this.showAlertSuccess(
          this.$t('INBOX_MGMT.ADD.EVOLUTION_API.UNLINK_NUMBER.SUCCESS')
        );
      } catch (error) {
        this.showAlertError(
          this.$t('INBOX_MGMT.ADD.EVOLUTION_API.UNLINK_NUMBER.ERROR')
        );
        console.error(error.message);
      }
    },
    // Método para actualizar el inbox con los nuevos valores
    async updateInbox() {
      this.isUpdating = true;
      try {
        const payload = {
          id: this.inbox.id,
          name: this.selectedInboxName,
          enable_data_extraction: this.enableDataExtraction,
          channel: {
            allow_send_messages: this.allowSendMessages,
          },
        };
        if (this.avatarFile) {
          payload.avatar = this.avatarFile;
        }

        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(
          error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE')
        );
      }
      this.isUpdating = false;
    },
    // Actualiza el valor del atributo linked en el state y la BD
    async updateLinkedNumberStatus() {
      try {
        const payload = {
          id: this.inbox.id,
          channel: {
            linked: this.linked,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
      } catch (error) {
        console.error(error.message);
      }
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async handleAvatarDelete() {
      try {
        await this.$store.dispatch(
          'inboxes/deleteInboxAvatar',
          this.currentInboxId
        );
        this.avatarFile = null;
        this.avatarUrl = '';
        this.showAlert(this.$t('INBOX_MGMT.DELETE.API.AVATAR_SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(
          error.message
            ? error.message
            : this.$t('INBOX_MGMT.DELETE.API.AVATAR_ERROR_MESSAGE')
        );
      }
    },
  },
  validations: {
    selectedInboxName: { required, maxLength: maxLength(30) },
  },
};
</script>
<style lang="scss" scoped>
.settings-item {
  padding-bottom: var(--space-normal);

  .help-text {
    font-style: normal;
    color: var(--b-500);
    padding-bottom: var(--space-smaller);
  }
}
</style>
